import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, X, ExternalLink, Disc3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { reviewsList, reviewsSetMode, reviewsRerun, reviewsRemind, reviewsDismiss, reviewsOverride, reviewsSweep } from "@/lib/afmApi";
import ReleaseFixPanel from "@/components/ReleaseFixPanel";
import { requestTakedown, getAdminReleases, setReleaseStatus, getAdminArtists, notifyReleaseStatus, adamantemSubmissions, adamantemSyncSona, adamantemGet, listReleaseRequests, decideReleaseRequest, markAdamantemTakedownDone, refreshAdamantemRequest, listArchivedReleases, setReleaseArchived, adamantemAdjustGet, adamantemAdjustSave, adamantemCopyCreate, adamantemCopyDelete } from "@/lib/afmApi";
import ReleaseReports from "@/components/ReleaseReports";
import AdamantemPanel, { AdamantemBadge, SubmissionDetail, ADAMANTEM_STATE } from "@/components/AdamantemPanel";
import AssignToManager from "@/components/AssignToManager";
import { fmtDate } from "@/lib/format";
import { Loading, SectionTitle } from "@/components/common";
import { StatusBadge, RELEASE_STATUS } from "@/components/StatusBadge";

const NOTIFY_STATUSES = ["approved", "distributed", "takedown", "needs_correction"];

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "draft", label: "Borrador" },
  { key: "in_review", label: "En revisión" },
  { key: "processing", label: "Procesando" },
  { key: "needs_correction", label: "Correcciones" },
  { key: "approved", label: "Aprobados" },
  { key: "distributed", label: "En tiendas" },
  { key: "takedown", label: "Retirados" },
];

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";

// Detalle completo del envío (clic en el título o el artista).
function DetailDialog({ release, onClose, onManage }) {
  const [sub, setSub] = useState(undefined);
  useEffect(() => { adamantemGet(release.id).then((d) => setSub(d?.state ? d : null)).catch(() => setSub(null)); }, [release.id]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="release-detail-dialog" onClick={onClose}>
      <div className="my-8 w-full max-w-2xl rounded-2xl border border-slate-300 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
              {release.cover_url ? <img src={release.cover_url} alt="" className="h-full w-full object-cover" /> : <Disc3 className="m-3 h-6 w-6 text-slate-300" />}
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{release.artist_name}</div>
              <h2 className="font-serif text-xl font-bold text-black">{release.title}</h2>
            </div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
        </div>
        <div className="space-y-3 p-6 text-sm">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-slate-500">Estado:</span> <StatusBadge map={RELEASE_STATUS} status={release.status} fallbackLabel={release.status_label} />
            <span className="text-xs text-slate-500">la distribuidora:</span> <AdamantemBadge state={sub?.state} />
          </div>
          {sub?.adamantem_id && <p className="font-mono text-[11px] text-slate-400">ID de distribución: {sub.adamantem_id}</p>}
          {sub === undefined ? <p className="text-slate-400">Cargando…</p>
            : sub ? <><SubmissionDetail sub={sub} />{sub.state === "forwarded" && <ReleaseReports releaseId={release.id} isAdmin />}</>
            : <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-500">Este lanzamiento aún no se ha enviado a la distribuidora. Usa "Gestionar" para revisarlo y aprobarlo.</p>}
        </div>
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cerrar</button>
          <button onClick={onManage} className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white">Gestionar</button>
        </div>
      </div>
    </div>
  );
}

const REASON_LABEL = { duplicate: "Duplicado", metadata_error: "Error en datos o portada", no_longer: "Ya no quiere publicarlo", rights: "Problema de derechos", other: "Otro motivo" };

const ADM_REQ = { pending: ["Enviada, pendiente", "bg-slate-100 text-slate-700"], in_review: ["En revisión", "bg-blue-100 text-blue-800"], accepted: ["Aceptada (aún no ejecutada)", "bg-emerald-100 text-emerald-800"], rejected: ["Rechazada por la distribuidora", "bg-red-100 text-red-800"], completed: ["Completada", "bg-emerald-600 text-white"], error: ["No se pudo enviar", "bg-amber-100 text-amber-900"] };

function AdamantemRequestStatus({ req, onChanged }) {
  const [ar, setAr] = useState(req.adamantem_request);
  const [busy, setBusy] = useState(false);
  if (!ar) return null;
  const [label, cls] = ADM_REQ[ar.status] || [ar.status, "bg-slate-100 text-slate-700"];
  const refresh = async () => {
    setBusy(true);
    try { const r = await refreshAdamantemRequest(req.id); setAr(r.adamantem_request || ar); toast.success("Actualizado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo consultar"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="adamantem-request-status">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{ar.type === "correction" ? "Corrección" : "Takedown"} en la distribuidora</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>
        {ar.id && <button onClick={refresh} disabled={busy} className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold hover:border-black disabled:opacity-50">{busy ? "Consultando…" : "Actualizar"}</button>}
      </div>
      {ar.id && <div className="mt-1 font-mono text-[11px] text-slate-500">Solicitud {ar.id}</div>}
      {ar.admin_notes && <div className="mt-1 text-xs text-slate-700">Nota de la distribuidora: {ar.admin_notes}</div>}
      {ar.error && <div className="mt-1 text-xs text-amber-800">{ar.error}</div>}
      {ar.checked_at && <div className="mt-1 text-[11px] text-slate-400">Consultado: {new Date(ar.checked_at).toLocaleString("es-VE")}</div>}
    </div>
  );
}

function TakedownReview({ req, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (approve) => {
    setBusy(true);
    try {
      const r = await decideReleaseRequest(req.id, { approve, admin_note: note.trim() });
      const ar = r?.adamantem_request;
      toast.success(!approve ? "Solicitud rechazada"
        : ar && ar.status !== "error" ? "Aprobada y enviada a la distribuidora"
        : r?.adamantem_takedown_needed ? "Aprobada. No se pudo enviar a la distribuidora: pídelo manualmente."
        : req.kind === "correction" ? "Corrección aprobada" : "Lanzamiento retirado");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); } finally { setBusy(false); }
  };
  const done = async () => {
    setBusy(true);
    try { await markAdamantemTakedownDone(req.id); toast.success("Marcado"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo marcar"); } finally { setBusy(false); }
  };
  if (req.state === "pending") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm" data-testid="takedown-review">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-red-700">{req.kind === "correction" ? "Corrección solicitada por el artista" : "Retiro solicitado por el artista"}</div>
        <div className="text-red-900"><strong>{REASON_LABEL[req.reason] || req.reason}</strong>{req.message ? ` — ${req.message}` : ""}</div>
        <div className="mt-1 text-[11px] text-red-700/80">{req.requested_by} · {new Date(req.created_at).toLocaleString("es-VE")}</div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota para el artista (opcional)" className="mt-3 w-full resize-none rounded-lg border border-red-200 bg-white px-3 py-2 text-xs outline-none focus:border-red-400" />
        <div className="mt-2 flex gap-2">
          <button onClick={() => act(true)} disabled={busy} data-testid="takedown-approve" className="rounded-lg bg-red-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{req.kind === "correction" ? "Aprobar y enviar a la distribuidora" : "Aprobar retiro"}</button>
          <button onClick={() => act(false)} disabled={busy} data-testid="takedown-reject" className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-black disabled:opacity-50">Rechazar</button>
        </div>
      </div>
    );
  }
  if (req.state === "approved" && req.adamantem_request && req.adamantem_request.status !== "error") {
    return <AdamantemRequestStatus req={req} onChanged={onChanged} />;
  }
  if (req.state === "approved" && req.adamantem_takedown?.needed && !req.adamantem_takedown?.done) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="takedown-adamantem-pending">
        <div className="font-semibold">No se pudo enviar la solicitud a la distribuidora</div>
        {req.adamantem_request?.error && <div className="mt-1 text-xs">{req.adamantem_request.error}</div>}
        <div className="mt-1 text-xs">Pídeselo por correo (ID <span className="font-mono">{req.adamantem_takedown.adamantem_id}</span>) y luego márcalo aquí.</div>
        <button onClick={done} disabled={busy} className="mt-2 rounded-lg bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Ya se lo pedí a la distribuidora</button>
      </div>
    );
  }
  return null;
}

// Tiendas (igual que el formulario "Distribuir Música").
const ADJ_DSPS = ["Spotify", "Apple Music", "Amazon Music", "YouTube Music", "Deezer", "Tidal", "TikTok", "Instagram / Reels", "YouTube Content ID", "Pandora"];
const adjErr = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "No se pudo guardar"; };

function StorePicker({ mode, stores, onMode, onStores }) {
  const toggle = (d) => onStores(stores.includes(d) ? stores.filter((x) => x !== d) : [...stores, d]);
  return (
    <div>
      <select value={mode} onChange={(e) => onMode(e.target.value)} className={inputCls}>
        <option value="">Lo que eligió el artista</option>
        <option value="global">Todas las tiendas (global)</option>
        <option value="custom">Solo algunas tiendas</option>
      </select>
      {mode === "custom" && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ADJ_DSPS.map((d) => (
            <button type="button" key={d} onClick={() => toggle(d)} className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${stores.includes(d) ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}>{d}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// Ajustar fecha/tiendas antes de enviar y hacer copias (p. ej. TikTok primero).
function AdjustSend({ release, onChanged }) {
  const [view, setView] = useState(null);
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("");
  const [stores, setStores] = useState([]);
  const [cLine, setCLine] = useState("");
  const [pLine, setPLine] = useState("");
  const [busy, setBusy] = useState(false);
  const [copyForm, setCopyForm] = useState(null);
  const [openCopy, setOpenCopy] = useState(null);
  const apply = (v) => {
    setView(v);
    setDate((v?.override?.release_date || release.release_date || "").slice(0, 10));
    setMode(v?.override?.distribution_mode || "");
    setStores(v?.override?.dsp_channels || []);
    setCLine(v?.override?.c_line || "");
    setPLine(v?.override?.p_line || "");
  };
  const load = useCallback(() => adamantemAdjustGet(release.id).then(apply).catch(() => setView({ error: true })), [release.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!view) return null;
  if (view.error) return null;
  const sent = view.state && view.state !== "error";
  const save = async () => {
    setBusy(true);
    try {
      const body = { release_date: date, c_line: cLine.trim(), p_line: pLine.trim() };
      if (mode) { body.distribution_mode = mode; body.dsp_channels = mode === "custom" ? stores : []; }
      apply(await adamantemAdjustSave(release.id, body)); toast.success("Envío ajustado. Revisa el envío antes de mandarlo."); onChanged && onChanged();
    } catch (e) { toast.error(adjErr(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    try { apply(await adamantemAdjustSave(release.id, { reset: true })); toast.success("Vuelve a lo que envió el artista"); onChanged && onChanged(); } catch (e) { toast.error(adjErr(e)); }
  };
  const createCopy = async () => {
    setBusy(true);
    try {
      const r = await adamantemCopyCreate(release.id, { label: copyForm.label, release_date: copyForm.date, distribution_mode: "custom", dsp_channels: copyForm.stores });
      apply({ ...view, copies: r.copies }); setCopyForm(null); setOpenCopy(r.release_id); toast.success("Copia creada. Revísala y envíala cuando quieras.");
    } catch (e) { toast.error(adjErr(e)); } finally { setBusy(false); }
  };
  const removeCopy = async (c) => {
    if (!window.confirm(`¿Borrar "${c.label}"? Aún no se ha enviado.`)) return;
    try { const v = await adamantemCopyDelete(c.release_id); apply({ ...view, copies: v.copies }); } catch (e) { toast.error(adjErr(e)); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="adjust-send">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Ajustar envío</div>
      {sent ? (
        <p className="text-xs text-slate-500">Ya se envió a la distribuidora. Para cambiar la fecha o las tiendas hay que pedirle una corrección a la distribuidora.</p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Fecha de salida</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
            <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Tiendas</label><StorePicker mode={mode} stores={stores} onMode={setMode} onStores={setStores} /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Línea © (C Line)</label><input value={cLine} onChange={(e) => setCLine(e.target.value)} placeholder={`${new Date().getFullYear()} Titular de la composición`} className={inputCls} /></div>
            <div><label className="mb-1 block text-[11px] font-semibold text-slate-500">Línea ℗ (P Line)</label><input value={pLine} onChange={(e) => setPLine(e.target.value)} placeholder={`${new Date().getFullYear()} Always Fresh Music`} className={inputCls} /></div>
          </div>
          <p className="text-[11px] text-slate-500">Déjalas vacías para usar las que puso el artista. Si faltan, la distribuidora no acepta el envío.</p>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={busy || !date} className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Guardar ajuste</button>
            {view.override && <button onClick={reset} className="text-xs font-semibold text-slate-500 hover:text-black">Volver a lo del artista</button>}
          </div>
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Copias ({view.copies.length})</span>
          {!copyForm && <button onClick={() => setCopyForm({ label: "TikTok primero", date: "", stores: ["TikTok", "Instagram / Reels"] })} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:border-black" data-testid="make-copy">Hacer una copia</button>}
        </div>
        {copyForm && (
          <div className="mb-3 space-y-2 rounded-lg bg-slate-50 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <input value={copyForm.label} onChange={(e) => setCopyForm({ ...copyForm, label: e.target.value })} placeholder="Nombre de la copia" className={inputCls} />
              <input type="date" value={copyForm.date} onChange={(e) => setCopyForm({ ...copyForm, date: e.target.value })} className={inputCls} />
            </div>
            <StorePicker mode="custom" stores={copyForm.stores} onMode={() => {}} onStores={(s) => setCopyForm({ ...copyForm, stores: s })} />
            <p className="text-[11px] text-slate-500">Mismas canciones, audio, portada e ISRC; la distribuidora le asigna un UPC nuevo.</p>
            <div className="flex gap-2">
              <button onClick={createCopy} disabled={busy || !copyForm.date || !copyForm.stores.length} className="rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Crear copia</button>
              <button onClick={() => setCopyForm(null)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs">Cancelar</button>
            </div>
          </div>
        )}
        {view.copies.map((c) => (
          <div key={c.release_id} className="mb-2 rounded-lg border border-slate-200">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
              <span className="font-semibold text-black">{c.label}</span>
              <span className="text-slate-500">{fmtDate(c.release_date)} · {(c.dsp_channels || []).join(", ") || "global"}</span>
              <span className="ml-auto" />
              <button onClick={() => setOpenCopy(openCopy === c.release_id ? null : c.release_id)} className="font-semibold text-slate-600 hover:text-black">{openCopy === c.release_id ? "Cerrar" : "Revisar / enviar"}</button>
              {(!c.state || c.state === "error") && <button onClick={() => removeCopy(c)} className="font-semibold text-red-600 hover:text-red-800">Borrar</button>}
            </div>
            {openCopy === c.release_id && <div className="border-t border-slate-100 p-2"><AdamantemPanel releaseId={c.release_id} releaseTitle={`${release.title} · ${c.label}`} onChange={load} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Revisión automática (reglas + IA): lo que está detenido y por qué, para presionar al artista.
const REVIEW_LABEL = {
  needs_fix: { t: "Debe corregir el artista", c: "border-red-200 bg-red-50 text-red-700" },
  manual: { t: "Decide el equipo", c: "border-amber-200 bg-amber-50 text-amber-800" },
  approved: { t: "Listo para enviar", c: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  error: { t: "No se pudo revisar", c: "border-slate-200 bg-slate-50 text-slate-600" },
};
const MODE_LABEL = { auto: "Automático: aprueba y envía solo", review: "Solo revisa: tú envías", off: "Apagado" };

function AutoReviewPanel({ onOpen, onData, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => reviewsList().then((d) => { setData(d); onData && onData(d?.reviews || []); })
    .catch(() => setData({ reviews: [], error: true })), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return null;
  const act = async (id, fn, ok) => {
    setBusy(id);
    try { const r = await fn(id); toast.success(typeof ok === "function" ? ok(r) : ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); } finally { setBusy(""); }
  };
  // "Revisar de nuevo" solo vuelve a revisar: si no se cambió nada, el resultado es el mismo.
  const RERUN_MSG = {
    needs_fix: ["error", "Sigue detenido: primero corrige lo de la lista (Gestionar › Corregir lanzamiento)"],
    manual: ["warn", "Lo tienes que decidir tú: Gestionar › Revisar envío"],
    approved: ["ok", "Aprobado: envíalo con Gestionar › Revisar envío"],
    already_sent: ["ok", "Ya está en la distribuidora"],
    off: ["warn", "La revisión automática está apagada"],
    busy: ["warn", "Se está revisando ahora mismo; espera un momento"],
    error: ["error", "No se pudo revisar; intenta de nuevo"],
  };
  const rerun = async (id) => {
    setBusy(id);
    try {
      const x = await reviewsRerun(id);
      const [kind, text] = x?.send === "sent" ? ["ok", "Aprobado y enviado a la distribuidora"] : (RERUN_MSG[x?.status] || ["ok", "Revisado"]);
      (kind === "error" ? toast.error : kind === "warn" ? toast.warning : toast.success)(text);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar"); } finally { setBusy(""); }
  };
  const override = async (r) => {
    const why = (r.issues || []).join("\n• ");
    if (!window.confirm(`La IA marcó:\n• ${why || "(sin detalle)"}\n\n¿Lo revisaste y está bien? Se aprueba sin la IA y, si las reglas básicas están en orden, se envía a la distribuidora.\n\nOjo: las tiendas pueden rechazar portadas con marcas de agua o imágenes sin licencia.`)) return;
    setBusy(r.release_id);
    try {
      const x = await reviewsOverride(r.release_id);
      const [kind, text] = x?.send === "sent" ? ["ok", "Aprobado y enviado a la distribuidora"] : (RERUN_MSG[x?.status] || ["ok", "Aprobado"]);
      (kind === "error" ? toast.error : kind === "warn" ? toast.warning : toast.success)(text);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); } finally { setBusy(""); }
  };
  const setMode = async (m) => { try { await reviewsSetMode(m); toast.success("Modo actualizado"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar"); } };
  const rows = data.reviews || [];
  const pend = rows.filter((r) => r.status === "needs_fix").length;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="auto-review-panel">
      <div className="flex flex-wrap items-center gap-3 px-5 py-3">
        <button onClick={() => setOpen(!open)} className="font-semibold text-black">Revisión automática ({rows.length})</button>
        {pend > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">{pend} esperando corrección del artista</span>}
        <span className="ml-auto" />
        <select value={data.mode || "auto"} disabled={data.mode_locked} onChange={(e) => setMode(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs" title={data.mode_locked ? "Fijado en el servidor (AUTO_REVIEW_MODE)" : ""}>
          {Object.entries(MODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {open && (
        <div className="border-t border-slate-100">
          {!rows.length && <p className="px-5 py-4 text-xs text-slate-400">Nada detenido. Lo que pasa la revisión sale solo a la distribuidora.</p>}
          {rows.map((r) => {
            const L = REVIEW_LABEL[r.status] || REVIEW_LABEL.error;
            return (
              <div key={r.release_id} className="border-b border-slate-100 px-5 py-3 last:border-0">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-black">{r.title || r.release_id}</span>
                  <span className="text-slate-500">· {r.artist}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${L.c}`}>{L.t}</span>
                  {r.days_waiting > 0 && <span className={`text-[11px] font-semibold ${r.days_waiting >= 3 ? "text-red-600" : "text-slate-500"}`}>{r.days_waiting} día(s) esperando</span>}
                  {r.reminders > 1 && <span className="text-[11px] text-slate-400">{r.reminders - 1} recordatorio(s)</span>}
                  <span className="ml-auto" />
                  {r.status === "needs_fix" && <button disabled={busy === r.release_id} onClick={() => act(r.release_id, reviewsRemind, (x) => `Recordatorio enviado · correo${x.app ? " · app" : ""}${x.whatsapp === "sent" ? " · WhatsApp" : ""}`)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:border-black">Recordar al artista</button>}
                  <button disabled={busy === r.release_id} onClick={() => rerun(r.release_id)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:border-black disabled:opacity-60">{busy === r.release_id ? "Revisando… (hasta 1 min)" : "Revisar de nuevo"}</button>
                  {(r.status === "needs_fix" || r.status === "manual") && !r.ai_override && <button disabled={busy === r.release_id} onClick={() => override(r)} className="rounded-lg border border-emerald-600 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60" data-testid="review-override">Aprobar igual</button>}
                  <button onClick={() => onOpen(r.release_id)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:border-black">Gestionar</button>
                  <button disabled={busy === r.release_id} onClick={() => act(r.release_id, reviewsDismiss, "Quitado de la lista")} className="text-xs text-slate-400 hover:text-black">Quitar</button>
                </div>
                {r.issues?.length > 0 && <ul className="mt-1.5 list-disc pl-5 text-xs text-red-700">{r.issues.map((i, k) => <li key={k}>{i}</li>)}</ul>}
                {r.ai?.famous_artists?.length > 0 && <p className="mt-1 text-xs text-amber-800">Artistas famosos detectados: <strong>{r.ai.famous_artists.join(", ")}</strong>. Confirma que tiene permiso antes de enviar.</p>}
                {r.status === "manual" && !r.ai?.available && <p className="mt-1 text-xs text-slate-500">La IA no respondió ({r.ai?.error || "sin detalle"}); revísalo a mano o dale "Revisar de nuevo".</p>}
                {r.ai?.summary && <p className="mt-1 text-[11px] text-slate-400">IA: {r.ai.summary}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ya enviado a la distribuidora: el cambio se pide como corrección (la distribuidora la aplica en las tiendas).
function AdminCorrection({ release, onDone }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (msg.trim().length < 10) { toast.error("Explica qué hay que corregir (mínimo 10 caracteres)"); return; }
    setBusy(true);
    try {
      const r = await requestTakedown({ release_id: release.id, kind: "correction", reason: "metadata_error", message: msg.trim() });
      const d = await decideReleaseRequest(r.id, { approve: true, admin_note: "Corrección enviada por el equipo" });
      const st = d?.adamantem_request?.status || d?.request?.adamantem_request?.status;
      toast.success(st === "error" ? "Corrección registrada, pero la distribuidora no la recibió: envíasela por correo" : "Corrección enviada a la distribuidora");
      setMsg(""); setOpen(false); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar la corrección"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="admin-correction">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Corregir un lanzamiento ya enviado</div>
        <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-slate-500 hover:text-black">{open ? "Cerrar" : "Abrir"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] text-slate-500">Ya está en la distribuidora, así que no se puede editar directo: se les manda una corrección y ellos la aplican en las tiendas. Di exactamente qué cambia (ej. "Título: Bb No Jales Bola", "Artista: DEIVI AF", "Productor: …").</p>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} className={`${inputCls} resize-none`} placeholder="Qué dato está mal y cómo debe quedar…" />
          <button onClick={send} disabled={busy} className="rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">{busy ? "Enviando…" : "Enviar corrección a la distribuidora"}</button>
        </div>
      )}
    </div>
  );
}

// Corregir desde la Bandeja (portada, título o artista) y volver a revisar al instante.
// Si queda bien y el modo es automático, sale solo a la distribuidora (y llega el correo).
function AdminFix({ release, review, sendDate, sendError, onDone }) {
  const held = review?.status === "needs_fix";
  const [open, setOpen] = useState(held || !!sendError);
  return (
    <div className={`rounded-xl border p-4 ${held ? "border-red-200 bg-red-50" : "border-slate-200"}`} data-testid="admin-fix">
      <div className="flex items-center justify-between gap-2">
        <div className={`text-xs font-semibold uppercase tracking-wider ${held ? "text-red-700" : "text-slate-600"}`}>{held ? "Detenido por la revisión automática" : "Corregir lanzamiento"}</div>
        {!held && <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-slate-500 hover:text-black">{open ? "Cerrar" : "Abrir"}</button>}
      </div>
      {!held && sendError && <p className="mt-2 text-xs text-red-700">La distribuidora lo devolvió: corrige el dato aquí y se vuelve a enviar.</p>}
      {held && review.issues?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-red-700">{review.issues.map((i, k) => <li key={k}>{i}</li>)}</ul>}
      <Link to={`/distribute?editar=${release.id}`} data-testid="admin-edit-release" className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-black">Editar todo el lanzamiento (formulario completo)</Link>
      {open && <ReleaseFixPanel admin releaseId={release.id} title={release.title || ""} artist={review?.artist || ""} date={sendDate || release.release_date || ""} migration={release.submission_type === "migration"} prevLabel={release.previous_label || ""} onDone={onDone} />}
    </div>
  );
}

function ModerateDialog({ release, viewerId, artistEmail, onClose, onDone, onAdamantem, admState, takedownReq, archived, onArchive, review, onReviewed, sendDate }) {
  const lockedByAdamantem = !!admState && admState !== "error";
  const [newStatus, setNewStatus] = useState(release.status);
  const [isrc, setIsrc] = useState(release.isrc || "");
  const [upc, setUpc] = useState(release.upc || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await setReleaseStatus(release.id, { viewer_id: viewerId, status: newStatus, isrc: isrc.trim(), upc: upc.trim(), note: note.trim() });
      if (newStatus !== release.status && NOTIFY_STATUSES.includes(newStatus) && artistEmail) {
        notifyReleaseStatus({ to: artistEmail, artist_name: release.artist_name, title: release.title, status: newStatus, note: note.trim(), detail_url: `${window.location.origin}/catalog/${release.id}` });
        toast.success("Lanzamiento actualizado · artista notificado por correo");
      } else {
        toast.success("Lanzamiento actualizado");
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="moderate-dialog">
      <div className="my-8 w-full max-w-lg rounded-2xl border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{release.artist_name}</div>
            <h2 className="font-serif text-xl font-bold text-black">{release.title}</h2>
          </div>
          <button onClick={onClose} data-testid="close-moderate"><X className="h-5 w-5 text-slate-400 hover:text-black" /></button>
        </div>
        <div className="space-y-4 p-6">
          {takedownReq && <TakedownReview req={takedownReq} onChanged={() => { onDone(); onAdamantem(); onClose(); }} />}
          {lockedByAdamantem && !takedownReq && <AdminCorrection release={release} onDone={() => { onDone(); onAdamantem(); }} />}
          {!lockedByAdamantem && <AdminFix release={release} review={review} sendDate={sendDate} sendError={admState === "error"} onDone={() => { onReviewed(); onAdamantem(); onDone(); }} />}
          <AdamantemPanel releaseId={release.id} releaseTitle={release.title} onChange={onAdamantem} />
          <AdjustSend release={release} onChanged={onAdamantem} />
          <AssignToManager release={release} />
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Estado</label>
            {lockedByAdamantem && <p className="mb-1.5 text-[11px] text-slate-500">Lo actualiza la distribuidora automáticamente ({ADAMANTEM_STATE[admState]?.label || admState}).</p>}
            <select data-testid="moderate-status" value={newStatus} disabled={lockedByAdamantem} onChange={(e) => setNewStatus(e.target.value)} className={`${inputCls} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}>
              {Object.entries(RELEASE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">ISRC</label>
              <input data-testid="moderate-isrc" value={isrc} onChange={(e) => setIsrc(e.target.value)} placeholder="Ej. VE-ABC-26-00001" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">UPC</label>
              <input data-testid="moderate-upc" value={upc} onChange={(e) => setUpc(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Nota (opcional)</label>
            <textarea data-testid="moderate-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Motivo de corrección o comentario para el artista…" className={`${inputCls} resize-none`} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          {onArchive && (!admState || admState === "error") && (
            <button onClick={() => onArchive(!archived)} data-testid="archive-release" className="mr-auto text-xs font-semibold text-slate-500 hover:text-black" title="Lo oculta de la bandeja sin borrarlo">
              {archived ? "Desarchivar" : "Archivar (ocultar de la bandeja)"}
            </button>
          )}
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:border-black">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="submit-moderate" className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReleases() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");
  const [releases, setReleases] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");
  const [active, setActive] = useState(null);
  const [emails, setEmails] = useState({});
  const [adm, setAdm] = useState({});
  const [admDates, setAdmDates] = useState({});
  const [admErr, setAdmErr] = useState({});
  const [takedowns, setTakedowns] = useState({});
  const [archivedIds, setArchivedIds] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [reviewsById, setReviewsById] = useState({});
  const [reviewKey, setReviewKey] = useState(0);
  const swept = useRef(false);
  const loadExtras = useCallback(() => {
    listReleaseRequests("all").then((rows) => {
      const m = {};
      rows.forEach((r) => { if (!m[r.release_id]) m[r.release_id] = r; }); // vienen de la más nueva a la más vieja
      setTakedowns(m);
    }).catch(() => {});
    listArchivedReleases().then(setArchivedIds).catch(() => {});
  }, []);
  useEffect(() => { loadExtras(); }, [loadExtras]);
  const archiveRelease = async (rel, archive) => {
    try {
      await setReleaseArchived(rel.id, archive, archive ? "Archivado desde la bandeja" : "");
      toast.success(archive ? "Archivado: ya no aparece en la bandeja" : "Desarchivado");
      loadExtras(); setActive(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo archivar"); }
  };

  const loadAdm = useCallback(() => {
    adamantemSubmissions()
      .then((d) => {
        const m = {}, errs = {};
        (d?.submissions || []).forEach((s) => { m[s.release_id] = s.state; if (s.state === "error" && s.last_error) errs[s.release_id] = s.last_error; });
        setAdm(m); setAdmErr(errs); setAdmDates(d?.dates || {});
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadAdm(); }, [loadAdm]);
  const [detail, setDetail] = useState(null);

  // Al abrir la bandeja: pasa a SONA los estados que cambiaron en la distribuidora (así los ve el artista).
  useEffect(() => {
    adamantemSyncSona().then((d) => { if (d?.synced) { load(); loadAdm(); } }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setStatus("loading");
    getAdminReleases(user.id, filter)
      .then((d) => { setReleases(d?.releases || []); setCounts(d?.counts || {}); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id, filter]);

  useEffect(() => { load(); }, [load]);

  // Una vez por visita: lo que está "en revisión" y nunca pasó por la revisión automática se revisa ahora.
  // Si está bien y el modo es automático, sale solo; lo que no, aparece arriba en "Revisión automática".
  useEffect(() => {
    if (swept.current || status !== "ok" || filter) return;
    swept.current = true;
    const ids = releases.filter((r) => r.status === "in_review").map((r) => r.id);
    if (!ids.length) return;
    reviewsSweep(ids).then((d) => {
      if (d?.queued) {
        toast.message(`Revisando ${d.queued} lanzamiento(s) pendiente(s)…`);
        setTimeout(() => { setReviewKey((k) => k + 1); loadAdm(); }, 30000);
      }
    }).catch(() => {});
  }, [status, filter, releases, loadAdm]);

  useEffect(() => {
    getAdminArtists(user.id)
      .then((d) => {
        const list = d?.artists || (Array.isArray(d) ? d : []);
        const map = {};
        list.forEach((a) => { if (a.user_id && a.email) map[a.user_id] = a.email; });
        setEmails(map);
      })
      .catch(() => {});
  }, [user.id]);

  if (status === "loading") return <Loading />;
  if (status === "denied") {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
        <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
        <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
        <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-releases-page">
      <SectionTitle>Bandeja de lanzamientos</SectionTitle>
      <AutoReviewPanel refreshKey={reviewKey} onData={(rows) => setReviewsById(Object.fromEntries(rows.map((x) => [x.release_id, x])))} onOpen={(id) => { const r = releases.find((x) => x.id === id); if (r) setActive(r); else toast.error("Ese lanzamiento no está en la lista actual"); }} />
      {Object.values(takedowns).some((t) => t.state === "pending" || (t.state === "approved" && t.adamantem_takedown?.needed && !t.adamantem_takedown?.done)) && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="takedown-banner">
          Tienes <strong>{Object.values(takedowns).filter((t) => t.state === "pending").length}</strong> solicitud(es) de retiro pendiente(s). Búscalas con la etiqueta <strong>"Retiro solicitado"</strong>.
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-500">
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" /> Mostrar archivados ({archivedIds.length})
      </label>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`release-filter-${f.key || "all"}`}
            onClick={() => setFilter(f.key)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}
          >
            {f.label}
            {f.key && counts[f.key] != null && <span className="ml-1.5 opacity-70">{counts[f.key]}</span>}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-3 font-semibold">Lanzamiento</th>
                <th className="px-3 py-3 font-semibold">Artista</th>
                <th className="px-3 py-3 font-semibold">ISRC</th>
                <th className="px-3 py-3 font-semibold">Fecha</th>
                <th className="px-3 py-3 text-center font-semibold">Estado</th>
                <th className="px-3 py-3 text-center font-semibold">Distribuidora</th>
                <th className="px-3 py-3 text-right font-semibold">Acción</th>
              </tr>
            </thead>
            <tbody>
              {releases.filter((r) => showArchived || !archivedIds.includes(r.id)).map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-release-row-${r.id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                        {r.cover_url ? <img src={r.cover_url} alt="" className="h-full w-full object-cover" /> : <Disc3 className="h-4 w-4 text-slate-300" />}
                      </div>
                      <div>
                        <button onClick={() => setDetail(r)} className="text-left font-medium text-black hover:underline" data-testid={`release-title-${r.id}`}>{r.title}</button>
                        {takedowns[r.id]?.state === "pending" && <span className="ml-2 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">{takedowns[r.id]?.kind === "correction" ? "Corrección solicitada" : "Retiro solicitado"}</span>}
                        {archivedIds.includes(r.id) && <span className="ml-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Archivado</span>}
                        <div className="text-[11px] uppercase tracking-wider text-slate-400">{r.submission_type === "migration" ? "Migración" : "Nuevo"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-600"><button onClick={() => setDetail(r)} className="text-left hover:text-black hover:underline">{r.artist_name}</button></td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.isrc || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{fmtDate(admDates[r.id] || r.release_date)}{admDates[r.id] && admDates[r.id] !== (r.release_date || "").slice(0, 10) && <span className="ml-1 text-[10px] text-amber-700" title="Fecha ajustada por el admin">(ajustada)</span>}</td>
                  <td className="px-3 py-3 text-center"><StatusBadge map={RELEASE_STATUS} status={r.status} fallbackLabel={r.status_label} /></td>
                  <td className="px-3 py-3 text-center"><AdamantemBadge state={adm[r.id]} />{admErr[r.id] && <div className="mx-auto mt-1 max-w-[220px] text-[10px] leading-tight text-red-600" title={admErr[r.id]} data-testid={`adm-error-${r.id}`}>{admErr[r.id].length > 120 ? `${admErr[r.id].slice(0, 120)}…` : admErr[r.id]}</div>}</td>
                  <td className="px-3 py-3 text-right">
                    <button data-testid={`moderate-btn-${r.id}`} onClick={() => setActive(r)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-black">Gestionar</button>
                  </td>
                </tr>
              ))}
              {!releases.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400">No hay lanzamientos en este filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {active && <ModerateDialog release={active} viewerId={user.id} artistEmail={emails[active.artist_id]} admState={adm[active.id]} takedownReq={takedowns[active.id]} archived={archivedIds.includes(active.id)} onArchive={(a) => archiveRelease(active, a)} onClose={() => { setActive(null); loadAdm(); loadExtras(); }} onDone={() => { load(); loadExtras(); }} onAdamantem={() => { loadAdm(); load(); }} review={reviewsById[active.id]} sendDate={admDates[active.id]} onReviewed={() => setReviewKey((k) => k + 1)} />}
      {detail && <DetailDialog release={detail} onClose={() => setDetail(null)} onManage={() => { setActive(detail); setDetail(null); }} />}
    </div>
  );
}
